import jsPDF from "jspdf";
import { headers, brokerHeaders } from "../components/TermsModal/TermsModal";

/**
 * Generate and download a PDF copy of the complete terms the user agreed to
 * Uses the exact content from headers and brokerHeaders arrays
 */
export const downloadTermsAgreement = (userInfo) => {
  const doc = new jsPDF();
  const downloadedAt = new Date();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const maxWidth = pageWidth - 2 * margin;
  const lineHeight = 5;
  let yPosition = margin;

  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace = 15) => {
    if (yPosition > pageHeight - margin - requiredSpace) {
      doc.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Helper function to extract text from React elements
  const extractTextFromContent = (content) => {
    if (typeof content === "string") {
      return content;
    }
    if (content?.props?.children) {
      const children = content.props.children;
      if (Array.isArray(children)) {
        return children.map((child) => extractTextFromContent(child)).join("");
      }
      return extractTextFromContent(children);
    }
    return "";
  };

  // Helper function to add text with wrapping
  const addWrappedText = (text, fontSize = 9, fontStyle = "normal") => {
    doc.setFontSize(fontSize);
    doc.setFont(undefined, fontStyle);

    // Remove HTML tags and decode entities
    const cleanText = text
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .trim();

    if (!cleanText) return;

    const lines = doc.splitTextToSize(cleanText, maxWidth);
    lines.forEach((line) => {
      checkNewPage();
      doc.text(line, margin, yPosition);
      yPosition += lineHeight;
    });
    yPosition += 2;
  };

  // ==================== COVER PAGE ====================
  // Add logo
  const logoPath = "/logo.png"; // Will be loaded from public folder
  const logoImg = new Image();
  logoImg.src = process.env.PUBLIC_URL + logoPath;

  // Add logo if available (centered at top)
  try {
    const logoWidth = 40;
    const logoHeight = 15;
    const logoX = (pageWidth - logoWidth) / 2;
    doc.addImage(logoImg, "PNG", logoX, yPosition, logoWidth, logoHeight);
    yPosition += logoHeight + 5;
  } catch (error) {
    // If logo fails to load, just continue without it
    console.warn("Logo could not be added to PDF");
  }

  doc.setFontSize(20);
  doc.setFont(undefined, "bold");
  doc.text("BPO Homes", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 10;

  doc.setFontSize(16);
  doc.text("Terms & Agreements", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 20;

  // User Information Box
  doc.setDrawColor(0, 102, 204);
  doc.setFillColor(240, 248, 255);
  const boxHeight = 52;
  doc.rect(margin, yPosition, maxWidth, boxHeight, "FD");

  yPosition += 10;
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text("Agreement Details:", margin + 5, yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.text(`Name: ${userInfo.userName || "N/A"}`, margin + 5, yPosition);
  yPosition += 7;
  doc.text(`Email: ${userInfo.email || "N/A"}`, margin + 5, yPosition);
  yPosition += 7;
  doc.text(
    `Role: ${userInfo.role?.replace(/_/g, " ")?.toUpperCase() || "N/A"}`,
    margin + 5,
    yPosition
  );
  yPosition += 7;
  doc.text(
    `Accepted On: ${
      userInfo.acceptedAt
        ? new Date(userInfo.acceptedAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
    }`,
    margin + 5,
    yPosition
  );
  yPosition += 7;
  doc.text(
    `Downloaded On: ${downloadedAt.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`,
    margin + 5,
    yPosition
  );

  yPosition += 15;

  // Acknowledgment Statement
  checkNewPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  addWrappedText(
    `I, ${
      userInfo.userName || "[User]"
    }, hereby acknowledge and confirm that I have read, understood, and agree to be bound by the following terms and agreements:`,
    11,
    "bold"
  );
  yPosition += 10;

  // ==================== TERMS OF SERVICE - Start on same page ====================
  checkNewPage(20); // Only add new page if not enough space

  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("BPO Homes Terms of Service", pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 12;

  // Process all sections from headers array
  headers.forEach((header) => {
    if (header.name) {
      checkNewPage(15);
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      addWrappedText(header.name, 12, "bold");
    }

    header.subs.forEach((sub) => {
      if (sub.content) {
        const textContent = extractTextFromContent(sub.content);
        if (textContent.trim()) {
          doc.setFontSize(9);
          doc.setFont(undefined, "normal");
          addWrappedText(textContent, 9, "normal");
        }
      }
    });
  });

  // ==================== BROKER AGREEMENT - Start on new page ====================
  doc.addPage();
  yPosition = margin;

  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("Master Broker Referral Agreement", pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 12;

  // Process all sections from brokerHeaders array
  brokerHeaders.forEach((header) => {
    if (header.name) {
      checkNewPage(15);
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      addWrappedText(header.name, 12, "bold");
    }

    header.subs.forEach((sub) => {
      if (sub.content) {
        const textContent =
          typeof sub.content === "string"
            ? sub.content
            : extractTextFromContent(sub.content);
        if (textContent.trim()) {
          doc.setFontSize(9);
          doc.setFont(undefined, "normal");
          addWrappedText(textContent, 9, "normal");
        }
      }
    });
  });

  // ==================== SIGNATURE PAGE ====================
  doc.addPage();
  yPosition = margin;

  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text("Signature and Acceptance", pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 15;

  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  addWrappedText(
    "By electronically accepting these terms, I acknowledge that I have read, understood, and agree to be bound by all provisions contained in the BPO Homes Terms of Service and the Master Broker Referral Agreement.",
    9,
    "normal"
  );

  yPosition += 10;
  doc.setFont(undefined, "bold");
  doc.text(`Signature: ${userInfo.userName || "N/A"}`, margin, yPosition);
  yPosition += 7;
  doc.text(
    `Date: ${
      userInfo.acceptedAt
        ? new Date(userInfo.acceptedAt).toLocaleDateString()
        : new Date().toLocaleDateString()
    }`,
    margin,
    yPosition
  );
  yPosition += 7;
  doc.text(
    `Date Downloaded: ${downloadedAt.toLocaleDateString()}`,
    margin,
    yPosition
  );

  yPosition = pageHeight - 30;
  doc.setFontSize(8);
  doc.setFont(undefined, "italic");
  doc.setTextColor(100);
  addWrappedText(
    "This document serves as a record of your acceptance of the BPO Homes Terms of Service and Master Broker Referral Agreement. For the complete and most current version of these agreements, please visit bpohomes.com/terms-of-service",
    8,
    "italic"
  );

  yPosition += 8;
  doc.setFont(undefined, "bold");
  doc.setTextColor(0);
  doc.text("BPO Homes Inc.", margin, yPosition);
  yPosition += 5;
  doc.setFont(undefined, "normal");
  doc.text(
    "6920 Santa Teresa Blvd Suite 208, San Jose, CA 95119",
    margin,
    yPosition
  );
  yPosition += 5;
  doc.text(
    "Email: contracts@bpohomes.com | Phone: 888.616.5270",
    margin,
    yPosition
  );

  // Generate filename with date
  const date = downloadedAt.toISOString().split("T")[0];
  const fileName = `BPO_Homes_Terms_Agreement_${userInfo.userName?.replace(
    /\s+/g,
    "_"
  )}_${date}.pdf`;

  // Save the PDF
  doc.save(fileName);
};
